import type { ChangeEvent } from 'react';
import type React from 'react';
import { useState } from 'react';
import classNames from 'classnames';
import styles from './AmountSelector.module.scss';
import Button, { Size } from '../../../general/Button/Button';

interface PriceOfAmountsProps {
  onAmountChange: (newAmount: string) => void;
  product: {
    amount: number;
    displayAmount: string;
  };
}

const AmountSelector: React.FC<PriceOfAmountsProps> = ({
  onAmountChange,
  product,
}) => {
  const [displayValue, setDisplayValue] = useState(product.displayAmount);
  const maxAmount = 999;
  const minAmount = 1;

  const updateAmount = (a: string) => {
    setDisplayValue(a);
    onAmountChange(a);
  };

  const reachedMaxAmount = (a: string) => parseInt(a, 10) >= maxAmount;

  const reachedMinAmount = (a: string) => (parseInt(a, 10) || 0) <= minAmount;

  const closeInput = (e: React.FocusEvent<HTMLInputElement>): void => {
    if (!e.target.value) {
      updateAmount(minAmount.toString());
    }
  };

  const changeManualAmount = (e: ChangeEvent<HTMLInputElement>) => {
    let target = e.target.value.replaceAll('.', '');

    if (target === '') {
      setDisplayValue(target);
      return;
    }

    if (reachedMinAmount(target)) {
      target = minAmount.toString();
    }

    if (reachedMaxAmount(target)) {
      target = maxAmount.toString();
    }

    updateAmount(target);
  };

  const increaseAmount = () => {
    updateAmount((parseInt(displayValue, 10) + 1).toString());
  };

  const decreaseAmount = () => {
    updateAmount((parseInt(displayValue, 10) - 1).toString());
  };

  return (
    <div className={classNames('form-control', styles.PriceOfAmounts__wrapper)}>
      <Button
        aria-label="decrease amount"
        isGhost
        iconOnly
        isClean
        size={Size.xs}
        icon="fa-minus"
        onClick={decreaseAmount}
        disabled={reachedMinAmount(displayValue)}
      />
      <input
        className={classNames(
          'form-input no-label js-form-input-no-validation',
          styles.PriceOfAmounts__input,
        )}
        value={displayValue}
        type="number"
        name="amount"
        onChange={changeManualAmount}
        onBlur={closeInput}
        max={maxAmount}
        min={minAmount}
      />
      <Button
        aria-label="increase amount"
        isGhost
        iconOnly
        icon="fa-plus"
        isClean
        size={Size.xs}
        onClick={increaseAmount}
        disabled={reachedMaxAmount(displayValue)}
      />
    </div>
  );
};

export default AmountSelector;
